import React from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import DataGrid, { DataGridItem } from "components/data-grid";
import { injectIntl } from "react-intl";
import withStyles from "components/with-styles";
import styles from "../styles/source-data.css.js";
import { TextDisplay } from "components/display-data";
import { SimpleButton } from "components/buttons";
import Chip from "components/chip";
import { Restricted, permissions } from "components/auth";
import {
  SOURCE_FUNCTION_STATE_ACTIVATED,
  SOURCE_FUNCTION_STATE_DEACTIVATED,
  SOURCE_FUNCTION_STATE_DISABLED,
  SOURCE_FUNCTION_STATE_ENABLED,
  SOURCE_FUNCTION_TYPE_ACTIVATE,
  SOURCE_FUNCTION_TYPE_DEACTIVATE,
  SOURCE_ICON_TYPE_CHECK,
  SOURCE_ICON_TYPE_CLOSE,
  SOURCE_TYPE_CAMERA,
  SOURCE_USE_FIXED,
  SOURCE_USE_FIXED_LEFT,
  SOURCE_USE_FIXED_RIGHT,
  SOURCE_USE_SCOREBOARD,
} from "../../../../utils/constants.js";

const BasicSourceData = ({
  classes,
  source,
  intl,
  onChangePrivacy,
  onChangeDisabled,
  onChangeDisabledIA,
  onChangeDisabledDewarp,
}) => {
  return !source ? null : (
    <>
      <DataGrid>
        <DataGridItem>
          <TextDisplay label={intl.formatMessage({ id: "common.type" })}>
            {source.type.toUpperCase()} {source.description}
          </TextDisplay>
        </DataGridItem>
        <DataGridItem>
          <TextDisplay label={intl.formatMessage({ id: "source.use" })}>{source.use.toUpperCase()}</TextDisplay>
        </DataGridItem>
        <DataGridItem>
          <TextDisplay label={intl.formatMessage({ id: "source.model" })}>
            <div>{source.brand + " " + source.model}</div>
          </TextDisplay>
        </DataGridItem>
        <DataGridItem>
          <TextDisplay label={intl.formatMessage({ id: "source.serial" })}>{source.serial}</TextDisplay>
        </DataGridItem>
        <DataGridItem>
          <TextDisplay label={intl.formatMessage({ id: "source.owner" })}>{source.owner}</TextDisplay>
        </DataGridItem>
        <DataGridItem>
          {source.use === SOURCE_USE_SCOREBOARD && source.type === SOURCE_TYPE_CAMERA && (
            <TextDisplay showIfEmpty label={intl.formatMessage({ id: "source.scoreboardPerspective" })}>
              {source.scoreboardPerspective}
            </TextDisplay>
          )}
        </DataGridItem>
      </DataGrid>

      <Restricted permissions={[permissions.source_view]}>
        <div>
          <Chip
            className={cn(classes.chip, { [classes.chip__activated]: source.privacyActivated })}
            variant="outlined"
            label={intl.formatMessage({
              id: `source.privacy.in.${
                source.privacyActivated ? SOURCE_FUNCTION_STATE_ACTIVATED : SOURCE_FUNCTION_STATE_DEACTIVATED
              }`,
            })}
          />
          <SimpleButton
            onClick={onChangePrivacy}
            className={classes.chipButton}
            icon={source.privacyActivated ? SOURCE_ICON_TYPE_CLOSE : SOURCE_ICON_TYPE_CHECK}
            label={intl.formatMessage({
              id: `source.operations.changePrivacy.in.${
                source.privacyActivated ? SOURCE_FUNCTION_TYPE_DEACTIVATE : SOURCE_FUNCTION_TYPE_ACTIVATE
              }`,
            })}
          />
        </div>
      </Restricted>
      <Restricted permissions={[permissions.source_manage]}>
        {(source.type === SOURCE_USE_SCOREBOARD ||
          (source.type === SOURCE_TYPE_CAMERA && source.use === SOURCE_USE_SCOREBOARD)) && (
          <div>
            <Chip
              className={cn(classes.chip, { [classes.chip__activated]: source.disabled })}
              variant="outlined"
              label={intl.formatMessage({
                id: `source.disabled.in.${
                  source.disabled ? SOURCE_FUNCTION_STATE_DISABLED : SOURCE_FUNCTION_STATE_ENABLED
                }`,
              })}
            />
            <SimpleButton
              onClick={onChangeDisabled}
              className={classes.chipButton}
              icon={source.disabled ? "close" : SOURCE_ICON_TYPE_CHECK}
              label={intl.formatMessage({
                id: `source.operations.changeDisabled.in.${
                  source.disabled ? SOURCE_FUNCTION_STATE_DISABLED : SOURCE_FUNCTION_STATE_ENABLED
                }`,
              })}
            />
          </div>
        )}
        {source.type === SOURCE_TYPE_CAMERA && source.use === SOURCE_USE_SCOREBOARD && (
          <div>
            <Chip
              className={cn(classes.chip, { [classes.chip__activated]: source.iaDisabled })}
              variant="outlined"
              label={intl.formatMessage({
                id: `source.disabledIA.in.${
                  source.iaDisabled ? SOURCE_FUNCTION_STATE_DISABLED : SOURCE_FUNCTION_STATE_ENABLED
                }`,
              })}
            />
            <SimpleButton
              onClick={onChangeDisabledIA}
              className={classes.chipButton}
              icon={source.iaDisabled ? SOURCE_ICON_TYPE_CLOSE : SOURCE_ICON_TYPE_CHECK}
              label={intl.formatMessage({
                id: `source.operations.changeDisabledIA.in.${
                  !source.iaDisabled ? SOURCE_FUNCTION_STATE_DISABLED : SOURCE_FUNCTION_STATE_ENABLED
                }`,
              })}
            />
          </div>
        )}
        {source.type === SOURCE_TYPE_CAMERA &&
          (source.use === SOURCE_USE_FIXED ||
            source.use === SOURCE_USE_FIXED_LEFT ||
            source.use === SOURCE_USE_FIXED_RIGHT) &&
          source.dewarpDisabled !== null && (
            <div>
              <Chip
                className={cn(classes.chip, { [classes.chip__activated]: source.dewarpDisabled })}
                variant="outlined"
                label={intl.formatMessage({
                  id: `source.dewarpDisabled.${
                    !source.dewarpDisabled ? SOURCE_FUNCTION_STATE_DISABLED : SOURCE_FUNCTION_STATE_ENABLED
                  }`,
                })}
              />
              <SimpleButton
                onClick={onChangeDisabledDewarp}
                className={classes.chipButton}
                icon={source.dewarpDisabled ? SOURCE_ICON_TYPE_CLOSE : SOURCE_ICON_TYPE_CHECK}
                label={intl.formatMessage({
                  id: `source.operations.dewarpDisabled.in.${
                    source.dewarpDisabled ? SOURCE_FUNCTION_STATE_DISABLED : SOURCE_FUNCTION_STATE_ENABLED
                  }`,
                })}
              />
            </div>
          )}
      </Restricted>
    </>
  );
};

BasicSourceData.propTypes = {
  /**
   * Data to represent
   **/
  source: PropTypes.object,
  /**
   * Changes source privacy
   **/
  onChangePrivacy: PropTypes.func,
  /**
   * Callback to change disabled flag in source
   **/
  onChangeDisabled: PropTypes.func,
  /**
   * Callback to change disabledIA flag in source
   **/
  onChangeDisabledIA: PropTypes.func,
};

export default injectIntl(withStyles(styles)(BasicSourceData));
